import {css, Slider, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  DlMuiButton,
  DlMuiDynamicIcon,
  DlMuiTextField,
  GetStylesFunction,
  PopupWrapper,
  useApiMutation,
} from '@wallaby-ui/core';
import React, {useCallback, useMemo, useState} from 'react';

import {Formatters} from './LightFormatter';

import type {UserFilterDto} from './LightSavedFiltersBox';

const WEIGHT_MIN = 0;
const WEIGHT_MAX = 100;
const WEIGHT_DEFAULT = 1;

type AlertMarkDto = {weight: number};

type Props = {
  userFilter: UserFilterDto;
  config: ConfigObjectType;
  formatters: Formatters;
  /** Replaces the row in the saved-filters state (the BE answers a mark with the whole filter DTO). */
  onAlertChanged: (userFilter: UserFilterDto) => void;
  setSubmitLoading: (newLoading: boolean) => void;
  onError: () => void;
};

/**
 * The saved-filter-to-alert control (PDW-242): a bell on the saved-filter row that opens the alert weight
 * popover. What renders is decided by the BE per row (`alertState`, `canManage`), the FE only maps it:
 *
 * | alertState       | canManage | bell                                      |
 * |------------------|-----------|-------------------------------------------|
 * | null / RETIRED   | true      | grey, "Set as alert"                       |
 * | ACTIVE           | true      | red, "Edit alert"                          |
 * | ACTIVE           | false     | red, display-only, "Shared alert. Only…"   |
 * | null / RETIRED   | false     | nothing                                    |
 *
 * One POST covers both "Set alert" and "Update" (the same two columns), DELETE retires the alert and keeps
 * the filter. A shared filter's popover carries an inline note instead of a confirmation dialog.
 */
export const LightSavedFilterAlertBell: React.FC<Props> = (props) => {
  const {userFilter, config, formatters, onAlertChanged, setSubmitLoading, onError} = props;
  const {t} = formatters;
  const theme = useTheme();
  const styles = getStyles({theme});
  const isGlobal = Boolean(userFilter.isGlobal);
  const isAlert = userFilter.alertState === 'ACTIVE';
  const canManage = Boolean(userFilter.canManage);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // the number input may be temporarily empty while typing, so it is kept as a string
  const [weightInput, setWeightInput] = useState(String(WEIGHT_DEFAULT));
  const weight = weightInput === '' ? NaN : Number(weightInput);
  const isWeightValid = Number.isInteger(weight) && weight >= WEIGHT_MIN && weight <= WEIGHT_MAX;

  const alertUrlTemplate: string = (isGlobal ? config.service.url.userFilterSharedAlert : config.service.url.userFilterAlert) ?? '';
  const alertUrl = alertUrlTemplate.replace('{userFilterId}', String(userFilter.id));
  const markData: AlertMarkDto = useMemo(() => ({weight: isWeightValid ? weight : WEIGHT_DEFAULT}), [isWeightValid, weight]);
  const onMarked = useCallback(
    (response: any) => {
      const marked: UserFilterDto = response && response.id ? response : {...userFilter, alertState: 'ACTIVE', weight: markData.weight};
      onAlertChanged(marked);
      setSubmitLoading(false);
    },
    [userFilter, markData.weight, onAlertChanged, setSubmitLoading]
  );
  const {mutate: mutateMark} = useApiMutation<AlertMarkDto>(
    [alertUrl, markData],
    markData,
    {
      reactMutationOptions: {
        onSuccess: onMarked,
        onError,
      },
      axiosConfig: {
        method: 'POST',
        url: alertUrl,
        data: markData,
      },
    },
    true
  );
  const onRetired = useCallback(() => {
    // the weight stays on the row on the BE and comes back on the next read, keep it here too
    onAlertChanged({...userFilter, alertState: 'RETIRED'});
    setSubmitLoading(false);
  }, [userFilter, onAlertChanged, setSubmitLoading]);
  const {mutate: mutateRetire} = useApiMutation(
    [alertUrl, 'retire'],
    null,
    {
      reactMutationOptions: {
        onSuccess: onRetired,
        onError,
      },
      axiosConfig: {
        method: 'DELETE',
        url: alertUrl,
      },
    },
    true
  );

  const open = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      // a retired alert reopens with the default weight, not the one it had (AC: "Default weight is 1")
      setWeightInput(String(isAlert ? userFilter.weight ?? WEIGHT_DEFAULT : WEIGHT_DEFAULT));
      setAnchorEl(event.currentTarget);
    },
    [isAlert, userFilter.weight]
  );
  const close = useCallback(() => setAnchorEl(null), []);
  const submitMark = useCallback(() => {
    if (!isWeightValid) return;
    mutateMark();
    setSubmitLoading(true);
    setAnchorEl(null);
  }, [isWeightValid, mutateMark, setSubmitLoading]);
  const submitRetire = useCallback(() => {
    mutateRetire();
    setSubmitLoading(true);
    setAnchorEl(null);
  }, [mutateRetire, setSubmitLoading]);
  // React events bubble through portals: without this every click inside the popover would also apply the filter
  const stopPropagation = useCallback((event: React.SyntheticEvent) => event.stopPropagation(), []);

  if (!isAlert && !canManage) return null;

  let bellTitle = 'filter.user.alert.sharedReadOnly';
  if (canManage) bellTitle = isAlert ? 'filter.user.alert.edit' : 'filter.user.alert.set';
  return (
    <>
      <DlMuiDynamicIcon
        iconName='bellIcon'
        useFixedViewBox='0 0 448 512'
        iconTitle={bellTitle}
        style={{color: 'inherit', padding: 0, margin: 0}}
        inputCss={styles.bellButton}
        svgIconInputCss={styles.bellIcon}
        noButtonStyles
        onClick={canManage ? open : undefined}
        data-name={'dl-mui-saved-filter--alert-bell'}
        data-alert={isAlert}
        data-can-manage={canManage}
      />
      {anchorEl && (
        <PopupWrapper
          openButton={false}
          anchorTag={anchorEl}
          onClose={close}
          direction='down'
          yOffset={4}
          contentStyle={{width: 238, gap: 0}}
          data-name={'dl-mui-saved-filter--alert-popup'}
        >
          <div css={styles.popup} onClick={stopPropagation} onMouseDown={stopPropagation}>
            <div css={styles.header}>
              <DlMuiDynamicIcon
                iconName='bellIcon'
                useFixedViewBox='0 0 448 512'
                noButtonStyles
                style={{color: 'inherit', padding: 0, margin: 0, pointerEvents: 'none'}}
                svgIconInputCss={styles.headerIcon}
              />
              {t('filter.user.alert.weight.title')}
            </div>
            {/* the note warns before a shared filter becomes everyone's alert, not on editing an alert it already is */}
            {isGlobal && !isAlert && (
              <div css={styles.sharedNote} data-name={'dl-mui-saved-filter--alert-shared-note'}>
                <DlMuiDynamicIcon
                  iconName='triangleExclamationIcon'
                  useFixedViewBox='0 0 512 512'
                  noButtonStyles
                  style={{color: 'inherit', padding: 0, margin: 0, pointerEvents: 'none'}}
                  svgIconInputCss={styles.sharedNoteIcon}
                />
                <span>{t('filter.user.alert.sharedNote')}</span>
              </div>
            )}
            <Slider
              css={styles.slider}
              size='small'
              min={WEIGHT_MIN}
              max={WEIGHT_MAX}
              step={1}
              value={isWeightValid ? weight : WEIGHT_MIN}
              onChange={(_event, value) => setWeightInput(String(Array.isArray(value) ? value[0] : value))}
              data-name={'dl-mui-saved-filter--alert-slider'}
            />
            <div css={styles.sliderLabels}>
              <span>{WEIGHT_MIN}</span>
              <span>{WEIGHT_MAX}</span>
            </div>
            <div css={styles.weightRow}>
              <DlMuiTextField
                variant='outlined'
                label=''
                type='number'
                value={weightInput}
                inputCss={styles.weightInput}
                inputProps={{'min': WEIGHT_MIN, 'max': WEIGHT_MAX, 'step': 1, 'data-name': 'dl-mui-saved-filter--alert-weight-input'}}
                onChange={(event) => setWeightInput(event.target.value ?? '')}
                error={!isWeightValid}
              />
              <span css={styles.help}>{t('filter.user.alert.weight.help')}</span>
            </div>
            <div css={isAlert ? styles.buttonsPair : styles.buttonsSingle}>
              {isAlert && (
                <DlMuiButton
                  type='chip-red'
                  title='filter.user.alert.removeButton'
                  onClick={submitRetire}
                  data-name={'dl-mui-saved-filter--alert-remove'}
                />
              )}
              <DlMuiButton
                type='chip-filled'
                title={isAlert ? 'filter.user.alert.updateButton' : 'filter.user.alert.setButton'}
                onClick={submitMark}
                disabled={!isWeightValid}
                data-name={'dl-mui-saved-filter--alert-confirm'}
              />
            </div>
          </div>
        </PopupWrapper>
      )}
    </>
  );
};

LightSavedFilterAlertBell.displayName = 'LightSavedFilterAlertBell';

const getStyles: GetStylesFunction = (_options) => ({
  /* takes the row's icon colours (--dlSavedFilterIcon and its hover, set on the row in LightSavedFiltersBox) like the
     share / delete icons; an active alert is red, the same on hover and on the selected row */
  bellButton: css`
    color: var(--dlSavedFilterIcon, var(--lightCheckbox)) !important;
    &:hover {
      color: var(--dlSavedFilterIconHover, var(--dlSavedFilterIcon, var(--lightCheckbox))) !important;
    }
    &[data-alert='true'],
    &[data-alert='true']:hover {
      color: var(--dlColorWarning) !important;
    }
    /* the read-only bell has no onClick, so DlMuiDynamicIcon forces 'default !important' on the button and 'default'
       on the svg; the attribute selector is more specific than both, so the design's help cursor wins */
    &[data-can-manage='false'],
    &[data-can-manage='false'] svg {
      cursor: help !important;
    }
  `,
  bellIcon: css`
    font-size: 14px;
  `,
  popup: css`
    display: flex;
    flex-direction: column;
    text-align: left;
    cursor: default;
    color: var(--dlThemeGray-800);
    font-family: var(--dlFontFamilyNormal);
  `,
  header: css`
    display: flex;
    align-items: center;
    gap: 7px;
    margin-bottom: 11px;
    font-family: var(--dlFontFamilySemiBold);
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--dlTheme-500);
  `,
  headerIcon: css`
    font-size: 12px;
  `,
  sharedNote: css`
    display: flex;
    gap: 7px;
    align-items: flex-start;
    background: var(--dlAlertNoteBackground);
    border-radius: 8px;
    padding: 8px 10px;
    margin-bottom: 12px;
    font-size: 11.5px;
    line-height: 1.45;
    color: var(--dlThemeGray-700);
  `,
  sharedNoteIcon: css`
    font-size: 12px;
    margin-top: 1px;
    color: var(--dlColorWarning);
  `,
  slider: css`
    width: 100%;
    margin: 0;
    padding: 10px 0;
    & .MuiSlider-track {
      background-color: var(--dlTheme-500);
      border: none;
    }
    & .MuiSlider-rail {
      background-color: var(--dlTheme-500);
    }
    & .MuiSlider-thumb {
      background-color: var(--dlTheme-500);
    }
  `,
  sliderLabels: css`
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: var(--dlThemeGray-400);
    margin: 0 2px 12px;
  `,
  weightRow: css`
    display: flex;
    align-items: center;
    gap: 9px;
    margin-bottom: 14px;
  `,
  weightInput: css`
    width: 64px !important;
    flex: none;
    & .MuiInputBase-root {
      height: 36px;
      border-radius: 6px;
    }
    & input {
      padding: 0 10px;
      font-size: 14px;
    }
  `,
  help: css`
    font-size: 11.5px;
    line-height: 1.4;
    color: var(--dlThemeGray-500);
  `,
  /* Remove + Update: two equal columns that both resolve to the wider label's natural width (same trick as
     PopupWrapper's yesNoWrapper), so the shorter label never looks like a smaller button. The popover keeps its
     fixed width, so the pair shares the content width (a label that would not fit gets the chip's ellipsis) */
  buttonsPair: css`
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 1fr;
    align-items: center;
    gap: 8px;
    width: 100%;
    & .MuiChip-root {
      width: 100%;
    }
  `,
  /* Set alert alone sits at the right edge like in the design */
  buttonsSingle: css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
  `,
});
